// useDtgRange.js — Ticket 02 (map-timeline-range-rework): editable DTG start/end range timeline.
//
// Local (non-persisted) state hook for the map editor's DTG timeline — a scrub position
// (`value`) bounded to a mutable `[start, end]` range that the user can edit via the timeline's
// two date pickers (`setStart`/`setEnd`). Consumers (MapTimeline, and everything MapEditorModal
// threads the scrub position into — `useMapEditor`'s `currentDtg`, `useSatelliteImagery`'s
// `dtgMs`, the Layers-panel filtering memo) keep reading the same public shape as the old
// `useDtgCursor` hook, with `start`/`end` now mutable instead of fixed constants:
//
//   const {
//     value,      // dayjs — current scrub value
//     valueMs,    // number — `value` as epoch ms, convenient for MUI <Slider value>
//     setValue,   // (next: dayjs | number) => void — jumps the scrub position, clamped to bounds
//     stepDay,    // (deltaDays: number) => void — moves the scrub position by `deltaDays` days
//     start,      // dayjs — lower bound (date-only, start of day)
//     end,        // dayjs — upper bound (date-only, start of day)
//     startMs,    // number — `start` as epoch ms, convenient for MUI <Slider min>
//     endMs,      // number — `end` as epoch ms, convenient for MUI <Slider max>
//     setStart,   // (next: dayjs | number) => void — moves the lower bound, date-only
//     setEnd,     // (next: dayjs | number) => void — moves the upper bound, date-only
//   } = useDtgRange({ start, end, initialValue });
//
// `start`/`end`/`initialValue` options accept anything dayjs(...) accepts (dayjs instance, ISO
// string, epoch ms). All options are optional. Defaults: `end` = today, `start` = today minus 30
// days — both truncated to start-of-day (date-only; no hours/minutes anywhere in this hook).
// Custom `start`/`end` options passed in by callers (tests, or any future non-default caller)
// are taken as-is, matching the old `useDtgCursor` hook's handling of its `start`/`end` options —
// only the *defaults* and anything set via `setStart`/`setEnd` (i.e. the date pickers) are forced
// to start-of-day.
//
// shared-map-data ticket 02: `start`/`end` here are the report's ONE shared DTG range (persisted
// on the shared map doc's `map_meta` node — every map node embed's editor shows/edits the same
// window), while `value`/`valueMs` stays a purely per-embed, per-hook-instance scrub position.
// This hook itself remains uncontrolled/self-contained (`start`/`end` options only seed the
// *initial* state, same as before) — callers that need to persist range edits elsewhere pass an
// optional `onRangeChange(startMs, endMs)` callback, invoked once per real `setStart`/`setEnd`
// call (never on mount, and never for `setValue`/`stepDay`) so they can push the edit into
// whatever shared storage they own (see MapEditorModal.jsx's `updateMeta` wiring).
//
// Bounds are mutually clamped so `start <= end` always holds — moving `start` past the current
// `end` pulls `end` along with it (and vice versa) — there are no other min/max bounds (no fixed
// historical floor, no "today" ceiling beyond that mutual clamp). The scrub `value` re-clamps
// into the (possibly just-moved) `[start, end]` whenever the bounds change, and defaults to `end`
// on initial mount.
//
// Bounds + scrub value are kept in a single state object (rather than three independent
// `useState`s) so every update — including the mutual-clamp cases above — is one atomic
// `setState` call; this sidesteps any ordering ambiguity between separately-queued updaters.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// map-node-dtg-timezone-fix ticket 01: day boundaries here are UTC-based (matching the embed's
// own already-UTC `<input type="date">` picker in MapNodeChrome.js — see that file's
// `msToDateInputValue`/`valueAsNumber` usage), not the browser's local timezone — see the
// `dayjs.extend(utc)` precedent in reportMeta.helpers.js.
dayjs.extend(utc);

function toMs(input) {
    return dayjs.isDayjs(input) ? input.valueOf() : dayjs(input).valueOf();
}

// Date-only: truncate to UTC start-of-day so the pickers (and the slider they bound) never
// carry hours/minutes, and so "day X" always means the same absolute instant regardless of the
// browser's local timezone.
function toStartOfDayMs(input) {
    return dayjs(toMs(input)).utc().startOf('day').valueOf();
}

function defaultEndMs() {
    return dayjs().utc().startOf('day').valueOf();
}

function defaultStartMs() {
    return dayjs().utc().subtract(30, 'day').startOf('day').valueOf();
}

function useDtgRange(options = {}) {
    const { start, end, initialValue, onRangeChange } = options;

    const [state, setState] = useState(() => {
        const startMs = toMs(start ?? defaultStartMs());
        const endMs = toMs(end ?? defaultEndMs());
        const initial = toMs(initialValue ?? end ?? defaultEndMs());
        const valueMs = Math.min(endMs, Math.max(startMs, initial));
        return { startMs, endMs, valueMs };
    });

    // Set to true (by setStart/setEnd) immediately before their setState call, and consumed (and
    // reset) by the effect below — this is how it's known that a `state.startMs`/`state.endMs`
    // change came from a real user edit rather than from mount, so `onRangeChange` fires exactly
    // once per edit and never spuriously on mount. Kept out of the setState updater itself (which
    // must stay a pure function of `prev`) rather than calling `onRangeChange` from inside it.
    const userEditedRangeRef = useRef(false);

    useEffect(() => {
        if (!userEditedRangeRef.current) return;
        userEditedRangeRef.current = false;
        onRangeChange?.(state.startMs, state.endMs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.startMs, state.endMs]);

    const setValue = useCallback((next) => {
        const ms = toMs(next);
        setState((prev) => {
            const valueMs = Math.min(prev.endMs, Math.max(prev.startMs, ms));
            return valueMs === prev.valueMs ? prev : { ...prev, valueMs };
        });
    }, []);

    const stepDay = useCallback((deltaDays) => {
        setState((prev) => {
            const next = dayjs(prev.valueMs).add(deltaDays, 'day').valueOf();
            const valueMs = Math.min(prev.endMs, Math.max(prev.startMs, next));
            return valueMs === prev.valueMs ? prev : { ...prev, valueMs };
        });
    }, []);

    // Mutual clamping only: moving `start` past the current `end` drags `end` along with it
    // (and vice versa in setEnd below) so `start <= end` always holds, with no other bounds. The
    // scrub value re-clamps into the resulting `[start, end]` in the same atomic update.
    //
    // shared-map-data ticket 02: marks `userEditedRangeRef` so the effect above fires
    // `onRangeChange(startMs, endMs)` (if supplied) with the resulting bounds — a caller can use
    // this to persist the shared range. Never fired on mount/from setValue/stepDay, only from an
    // actual setStart/setEnd call.
    const setStart = useCallback((next) => {
        userEditedRangeRef.current = true;
        setState((prev) => {
            const startMs = toStartOfDayMs(next);
            const endMs = startMs > prev.endMs ? startMs : prev.endMs;
            const valueMs = Math.min(endMs, Math.max(startMs, prev.valueMs));
            return { startMs, endMs, valueMs };
        });
    }, []);

    const setEnd = useCallback((next) => {
        userEditedRangeRef.current = true;
        setState((prev) => {
            const endMs = toStartOfDayMs(next);
            const startMs = endMs < prev.startMs ? endMs : prev.startMs;
            const valueMs = Math.min(endMs, Math.max(startMs, prev.valueMs));
            return { startMs, endMs, valueMs };
        });
    }, []);

    const { startMs, endMs, valueMs } = state;
    // map-node-dtg-timezone-fix ticket 01: exposed as UTC-mode dayjs instances (not plain local
    // ones) so consumers that read calendar fields off them directly (e.g. MapTimeline.jsx's
    // DatePickers) always see the intended UTC day, regardless of the browser's local timezone.
    // `.valueOf()` (the absolute instant) is unaffected by this — only display/field getters are.
    const value = useMemo(() => dayjs(valueMs).utc(), [valueMs]);
    const startDay = useMemo(() => dayjs(startMs).utc(), [startMs]);
    const endDay = useMemo(() => dayjs(endMs).utc(), [endMs]);

    // ticket 05 (map-editor-audit): all fields here are already individually stable (state
    // values, or callbacks with an empty dep array) — memoizing the returned object itself means
    // `MapTimeline`'s `range` prop keeps the same identity across unrelated MapEditorModal
    // re-renders, so wrapping it in React.memo there is actually effective.
    return useMemo(() => ({
        value, valueMs, setValue, stepDay,
        start: startDay, end: endDay, startMs, endMs,
        setStart, setEnd,
    }), [value, valueMs, setValue, stepDay, startDay, endDay, startMs, endMs, setStart, setEnd]);
}

export default useDtgRange;
